import * as Sentry from "@sentry/react";
import { JM_ENVIRONMENT, type RuntimeEnvironment } from "app/Firebase";
import { sentryLoggingEnabledAtom } from "app/devTools/devToolsConfigAtom";
import axios from "axios";
import { getDefaultStore } from "jotai";

type ErrorContext = Record<string, unknown>;

type CaptureAppErrorParams = {
  error: unknown;
  componentName?: string;
  consoleMessage?: string;
  fallbackMessage?: string;
  tags?: Record<string, string | number | boolean | undefined>;
  contexts?: Record<string, ErrorContext>;
};

type AddAppErrorBreadcrumbParams = {
  error: unknown;
  category: string;
  message: string;
  data?: ErrorContext;
};

const getAxiosError = (error: unknown) => {
  if (typeof axios.isAxiosError !== "function" || !axios.isAxiosError(error)) {
    return undefined;
  }

  return error;
};

export const getErrorStatusCode = (error: unknown) => {
  const axiosError = getAxiosError(error);

  return axiosError?.response?.status ?? axiosError?.status;
};

export const getErrorDetails = (error: unknown) => {
  const axiosError = getAxiosError(error);

  if (axiosError) {
    return {
      isAxiosError: true,
      name: axiosError.name,
      message: axiosError.message,
      code: axiosError.code,
      statusCode: axiosError.response?.status ?? axiosError.status,
      statusText: axiosError.response?.statusText,
      responseContentType: axiosError.response?.headers?.["content-type"],
      hasResponse: Boolean(axiosError.response),
      hasRequest: Boolean(axiosError.request),
    };
  }

  if (error instanceof Error) {
    return {
      isAxiosError: false,
      name: error.name,
      message: error.message,
    };
  }

  return {
    isAxiosError: false,
    name: typeof error,
    message: typeof error === "string" ? error : undefined,
  };
};

const toError = (error: unknown, fallbackMessage: string) => {
  if (error instanceof Error) {
    return error;
  }

  return new Error(typeof error === "string" ? error : fallbackMessage);
};

const getCallerComponentName = () => {
  const stackLines = new Error().stack?.split("\n") ?? [];
  const callerLine = stackLines.find(
    (line) =>
      !line.includes("errorReporting.") &&
      !line.includes("captureAppError") &&
      !line.includes("getCallerComponentName"),
  );
  const fileMatch = callerLine?.match(/([^/\\()]+)\.(tsx?|jsx?)(?:\?|:|$)/);

  return fileMatch?.[1] ?? "unknown";
};

export const isSentryLoggingEnabled = () =>
  getDefaultStore().get(sentryLoggingEnabledAtom);

export const shouldSendToSentry = (
  environment: RuntimeEnvironment = JM_ENVIRONMENT,
) => (environment === "DEVELOPMENT" ? isSentryLoggingEnabled() : true);

export const captureAppError = ({
  error,
  componentName,
  consoleMessage,
  fallbackMessage,
  tags = {},
  contexts = {},
}: CaptureAppErrorParams) => {
  const inferredComponentName = componentName ?? getCallerComponentName();

  if (consoleMessage) {
    console.error(consoleMessage, error);
  }

  if (!shouldSendToSentry()) {
    return undefined;
  }

  const errorDetails = getErrorDetails(error);

  return Sentry.withScope((scope) => {
    scope.setTag("component", inferredComponentName);

    Object.entries(tags).forEach(([key, value]) => {
      if (value !== undefined) {
        scope.setTag(key, String(value));
      }
    });

    scope.setContext("app_error", {
      componentName: inferredComponentName,
      ...errorDetails,
    });

    Object.entries(contexts).forEach(([name, context]) => {
      scope.setContext(name, context);
    });

    return Sentry.captureException(
      toError(
        error,
        fallbackMessage ?? `Unhandled error in ${inferredComponentName}`,
      ),
    );
  });
};

export const addAppErrorBreadcrumb = ({
  error,
  category,
  message,
  data = {},
}: AddAppErrorBreadcrumbParams) => {
  if (!shouldSendToSentry()) {
    return;
  }

  Sentry.addBreadcrumb({
    category,
    level: "warning",
    message,
    data: {
      ...data,
      error: getErrorDetails(error),
    },
  });
};
